import type { DivisionFormStgItemProps } from './division-form-stg.interface';

import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import cn from 'classnames';
import { Field } from 'formik';
import { useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';

import { usePermissions } from '../../../../contexts/permissions.context';
import * as utils from '../../../../shared/utils';
import { vocabulary } from '../../../../vocabulary/german';
import { TextEditor } from '../../../fields/text-editor/text-editor';
import { TextareaField } from '../../../fields/textarea-field/textarea-field';
import { ConfirmationModal } from '../../../template/modals/confirmation-modal';
import { DivisionFormActions } from '../division-form-actions/division-form-actions';

const GOAL_STATUSES = {
  0: 'notStarted',
  1: 'inProgress',
  2: 'finished',
};
const DivisionFormStgItem = ({
  shortTermGoalIndex,
  shortTermGoalsHelper,
  shortTermGoal,
  currentYearFrozen = false,
}: DivisionFormStgItemProps) => {
  const matrix = usePermissions();
  const reasonClass = cn(
    'priorities__report-icon',
    !!shortTermGoal.isShowReason && 'priorities__report-icon__disabled',
  );

  const activeStatus = GOAL_STATUSES[shortTermGoal.status];
  const isReadOnly = utils.getReadOnly();
  const changeReason = () => {
    shortTermGoal.isShowReason = !shortTermGoal.isShowReason;
  };
  const permissionsCanChange = utils.notChangeGoal();
  const [reasonFocusPosition, setReasonFocusPosition] = useState(0);

  const reasonEditor = useEditor({
    extensions: [StarterKit],
    content: shortTermGoal.reason,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      shortTermGoalsHelper.form.setFieldValue(
        `ShortTermGoals.${shortTermGoalIndex}.reason`,
        html,
      );
      setReasonFocusPosition(editor.state.selection.from);
    },
  });

  return (
    <div className="stg">
      <div className="stg-fields">
        <div className="stg-row-fields-upper">
          {
            <TextareaField
              name={`ShortTermGoals.${shortTermGoalIndex}.title`}
              placeholder={
                vocabulary.forms.shortTermGoals.placeholders.stgTitle
              }
              className={cn(
                'priorities__reason-field-wrapper',
                shortTermGoal.isPreviousYearGoal &&
                  'priorities__bg-color-this-year',
              )}
              disabled={permissionsCanChange}
              forDivision
              currentYearFrozen={currentYearFrozen}
            />
          }
          <TextareaField
            name={`ShortTermGoals.${shortTermGoalIndex}.outcome`}
            placeholder={
              vocabulary.forms.shortTermGoals.placeholders.stgOutcome
            }
            className={cn(
              'form__field_no-margin priorities__reason-field-wrapper',
              shortTermGoal.isPreviousYearGoal &&
                'priorities__bg-color-this-year',
            )}
            forDivision
            currentYearFrozen={currentYearFrozen}
          />
          <div className="field-wrapper field-wrapper_row field-wrapper_row_shrink">
            <span className="division-actions__main-status">
              {vocabulary.statuses[activeStatus]}
            </span>
            <div className="priorities__reason-wrapper">
              <span onClick={changeReason}>
                <Field
                  type="checkbox"
                  name={`ShortTermGoals.${shortTermGoalIndex}.isShowReason`}
                  disabled={isReadOnly}
                />
              </span>
              <i className={reasonClass} />
            </div>
            {(currentYearFrozen
              ? matrix.permissions.freezeRemoveFieldsInside === true
              : matrix.permissions.removeFieldsInside === true) && (
              <button
                type="button"
                data-controlled-permissions={DivisionFormStgItem.name}
                className="priorities__remove-icon"
                onClick={() => {
                  confirmAlert({
                    customUI: ({ onClose }) => {
                      return (
                        <ConfirmationModal
                          forDivision
                          onClose={onClose}
                          onOk={() =>
                            shortTermGoalsHelper.remove(shortTermGoalIndex)
                          }
                        />
                      );
                    },
                  });
                }}
              />
            )}
          </div>
        </div>
        {!!shortTermGoal.isShowReason && (
          <div className="stg-row-fields-lower priorities__reason-field-wrapper">
            <TextEditor
              position={reasonFocusPosition}
              editor={reasonEditor}
              commentText={shortTermGoal.reason || ''}
            />
          </div>
        )}
        <div className="stg-row-fields-lower">
          <DivisionFormActions
            currentYearFrozen={currentYearFrozen}
            shortTermGoal={shortTermGoal}
            shortTermGoalIndex={shortTermGoalIndex}
          />
        </div>
      </div>
    </div>
  );
};

export default DivisionFormStgItem;
